import { useState } from "react";
import { motion } from "framer-motion";
import { useNetworxServices } from "../../../../hooks/queries/useNetworxServices";
import CategoryCard from "./CategoryCard";
import { SlotsToClasses } from "@nextui-org/react";
import { cn } from "@/libs/utils/tailwind/cn";
import { isMobile } from "@/utils/is-mobile";
import Spinner from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";


export default function PopularCategoriesSection({
  className,
  classNames,
}: PopularCategoriesSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isMobileView = isMobile();
  const { data, isLoading } = useNetworxServices({
    sortBy: "order",
  });

  const mainServices = data?.mainServices ?? [];
  const services = data?.services ?? [];

  const defaultServiceIds = new Set(mainServices.map((service) => service.id));
  const allServices = [
    ...mainServices,
    ...services.filter((service) => !defaultServiceIds.has(service.id)),
  ];

  const displayedServices = isExpanded
    ? allServices
    : mainServices;

  const hasMoreServices = allServices.length > mainServices.length;

  const expandServices = () => {
    setIsExpanded(true);
  };

  return (
    <section
      className={cn(
        "w-full pt-6 sm:py-12",
        classNames?.base,
        className
      )}
    >
      <div className="flex items-center justify-between">
        <h2 className="pl-4 mb-1 sm:mb-10 text-xl sm:text-3xl font-bold text-foreground">
          Popular categories
        </h2>

        {!isLoading && !isMobileView && hasMoreServices && !isExpanded && (
          <Button
          variant="ghost"
          className="w-fit min-w-fit border-0 border-b-1 border-default-800 rounded-none leading-5 text-foreground text-lg text-right font-normal data-[hover=true]:!bg-transparent p-0 h-auto"
          onPress={expandServices}
        >
          View All
        </Button>
        )}
      </div>

      {isLoading ? (
        <div className="relative w-full h-[110px] flex items-center justify-center">
          <Spinner
            classNames={{
              svg: "h-10 w-10",
            }}
          />
        </div>
      ) : isMobileView ? (
        <div className="mb-1 pl-2.5 sm:pl-0 flex gap-2 overflow-x-auto">
          {allServices.map((service) => (
            <div
              key={service.id}
              className="flex-none w-[93px] h-[110px] sm:h-[150px] lg:h-[110px] [&>a]:size-full"
            >
              <CategoryCard service={service} />
            </div>
          ))}
        </div>
      ) : (
        <motion.div layout className="mb-8 grid grid-cols-8 gap-2">
          {displayedServices.map((service, index) => (
            <motion.div
              key={service.id}
              layout
              initial={
                isExpanded && index >= mainServices.length
                  ? { opacity: 0, y: 12 }
                  : false
              }
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.35,
                ease: "easeOut",
                delay:
                  isExpanded && index >= mainServices.length
                    ? (index - mainServices.length) * 0.04
                    : 0,
              }}
            >
              <CategoryCard service={service} />
            </motion.div>
          ))}
        </motion.div>
      )}
    </section>
  );
}

interface PopularCategoriesSectionProps {
  className?: string;
  classNames?: SlotsToClasses<"base" | "container">;
}